import type { PayloadAction } from '@reduxjs/toolkit'
import { createSlice } from '@reduxjs/toolkit'

import type { RootState } from '@web/app/store'
import type { IKinematicElement } from '@web/entities/kinematic/model/kinematic.type'
import { EKinematicViewMode } from '@web/shared/enums/kinematic/kinematic-view-mode.enum'
import { useDispatchedActions } from '@web/shared/hooks/use-dispatched-actions.hook'

interface IStageOptions {
  width: number
  height: number
  scale: number
}

export interface IKinematicUiState {
  viewMode: EKinematicViewMode
  selectedKinematics: IKinematicElement[]
  stageOptions: IStageOptions
}

const initialState: IKinematicUiState = {
  viewMode: EKinematicViewMode.Status,
  selectedKinematics: [],
  stageOptions: { width: 800, height: 600, scale: 1 }
}

export const kinematicSlice = createSlice({
  name: 'kinematic',
  initialState,
  reducers: {
    setViewMode(state, action: PayloadAction<EKinematicViewMode>) {
      state.viewMode = action.payload
    },
    setSelectedKinematics(state, action: PayloadAction<IKinematicElement[]>) {
      state.selectedKinematics = [...action.payload]
    },
    toggleSelectedKinematics(state, action: PayloadAction<IKinematicElement>) {
      const selectedBlock = action.payload
      const selectedBlocks = new Set(
        state.selectedKinematics?.map((item) => item.kinematicBlockId)
      )

      if (selectedBlocks.has(selectedBlock.kinematicBlockId)) {
        state.selectedKinematics = state.selectedKinematics?.filter(
          (item) => item.kinematicBlockId !== selectedBlock.kinematicBlockId
        )
      } else {
        state.selectedKinematics?.push(selectedBlock)
      }
    },
    clearSelectedKinematics(state) {
      state.selectedKinematics = []
    },
    incrementKSScale(state) {
      state.stageOptions.scale = state.stageOptions.scale + 0.1
    },

    decrementKSScale(state) {
      state.stageOptions.scale =
        state.stageOptions.scale >= 0.2
          ? state.stageOptions.scale - 0.1
          : state.stageOptions.scale
    },

    setStageKSOptions(
      state,
      { payload }: PayloadAction<Partial<IStageOptions>>
    ) {
      state.stageOptions = { ...state.stageOptions, ...payload }
    }
  }
})

export const kinematicReducer = kinematicSlice.reducer
export const selectSelectedKinematic = (state: RootState) => {
  const { selectedKinematics } = state.kinematic
  return selectedKinematics.length === 1 ? selectedKinematics[0] : null
}

export const useKinematicActions = () =>
  useDispatchedActions(kinematicSlice.actions)
